export type GstMode = 'none' | 'included' | 'excluded';
export type DiscountMode = 'none' | 'percentage' | 'flat';

export const DEFAULT_GST_RATE = 18;

export interface PaymentBreakdown {
  baseAmount: number;
  discountAmount: number;
  discountedAmount: number;
  gst: number;
  total: number;
}

const round2 = (n: number): number => Number(n.toFixed(2));

export const computeDiscount = (
  amount: number,
  discountMode: DiscountMode,
  discountValue: number,
): number => {
  if (!discountMode || discountMode === 'none' || !discountValue) return 0;
  if (discountMode === 'flat') {
    return Math.min(amount, Math.max(0, discountValue));
  }
  return amount * (Math.max(0, discountValue) / 100);
};

export const computePaymentBreakdown = (
  amount: number,
  discountMode: DiscountMode,
  discountValue: number,
  gstMode: GstMode,
  gstRate: number = DEFAULT_GST_RATE,
): PaymentBreakdown => {
  const baseAmount = Math.max(0, Number(amount) || 0);
  const discountAmount = round2(
    computeDiscount(baseAmount, discountMode, Number(discountValue) || 0),
  );
  const discountedAmount = round2(Math.max(0, baseAmount - discountAmount));

  let gst = 0;
  let total = discountedAmount;

  if (gstMode === 'excluded') {
    gst = round2((discountedAmount * gstRate) / 100);
    total = round2(discountedAmount + gst);
  } else if (gstMode === 'included') {
    const base = discountedAmount / (1 + gstRate / 100);
    gst = round2(discountedAmount - base);
    total = discountedAmount;
  }

  return { baseAmount, discountAmount, discountedAmount, gst, total };
};

export type PlanDiscountType = 'first_time' | 'all_time';

interface PricedPlan {
  price?: number | string;
  discountMode?: DiscountMode;
  discountValue?: number | string;
  discountType?: PlanDiscountType;
  gstMode?: GstMode;
}

/** `first` is what the first payment costs; `recurring` is what each later cycle costs. */
export const computePlanBreakdowns = (plan: PricedPlan, gstRate: number = DEFAULT_GST_RATE) => {
  const price = Number(plan.price) || 0;
  const gstMode = plan.gstMode || 'none';
  const first = computePaymentBreakdown(
    price,
    plan.discountMode || 'none',
    Number(plan.discountValue) || 0,
    gstMode,
    gstRate,
  );
  const full = computePaymentBreakdown(price, 'none', 0, gstMode, gstRate);
  return { first, recurring: (plan.discountType || 'all_time') === 'all_time' ? first : full };
};
